import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SearchIcon from '@mui/icons-material/Search';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { format } from 'date-fns';
import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAttachmentDetails } from 'api';
import { useFilesGalleryQuery } from 'api/queries/useFilesGalleryQuery';
import { FileItem } from 'api/types';
import { ReactComponent as CSVIcon } from 'assets/icons/fileType/csv.svg';
import { ReactComponent as ExcelIcon } from 'assets/icons/fileType/excel.svg';
import { ReactComponent as PPTIcon } from 'assets/icons/fileType/ppt.svg';
import { ReactComponent as TextIcon } from 'assets/icons/fileType/txt.svg';
import { ReactComponent as WordIcon } from 'assets/icons/fileType/word.svg';
import { ReactComponent as PlayIcon } from 'assets/icons/play.svg';
import { IMAGE_MIME_TYPES, VIDEO_MIME_TYPES } from 'components/MediaUploadZone/constants';
import { theme } from 'styles';

import {
  AddButton,
  CancelBtn,
  CardCheckboxWrapper,
  CardIconWrapper,
  CardMeta,
  CardTitle,
  EmptyStateWrapper,
  FileCardWrapper,
  FileGrid,
  ModalHeader,
  ModalTitle,
  PlayButtonContainer,
  SearchWrapper,
  StyledImage,
  StyledVideo,
  VideoW,
} from './styled';

type GalleryTab = 'documents' | 'images' | 'videos';

interface GalleryFileCardProps {
  file: FileItem;
  isSelected: boolean;
  onToggle: (file: FileItem) => void;
}

function GalleryFileCard({ file, isSelected, onToggle }: GalleryFileCardProps): ReactElement {
  const isImage = IMAGE_MIME_TYPES.includes(file.type) || file.type === 'image';
  const { data } = useAttachmentDetails({
    attachmentId: file.id,
    fileName: file.display_name,
    fileUrl: `${file.file_url.small}`,
    enabled: isImage,
  });

  const authorName = `${file.created_by?.first_name ?? ''} ${
    file.created_by?.last_name ?? ''
  }`.trim();
  const dateStr = file.created_at ? format(new Date(file.created_at), 'dd MMM yyyy') : '';
  let videoThumbnailurl = '';
  if (file?.file_url.video_uid) {
    const videoId = file?.file_url.video_uid;
    videoThumbnailurl = `https://customer-${process.env.REACT_APP_CLOUDFLARE_STREAM_ACCOUNT_ID}.cloudflarestream.com/${videoId}/thumbnails/thumbnail.gif?time=1s&height=200&duration=4s`;
  }

  return (
    <FileCardWrapper key={file.id} selected={isSelected} onClick={() => onToggle(file)}>
      <CardCheckboxWrapper onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={isSelected}
          onChange={() => onToggle(file)}
          size="small"
          color="info"
          sx={{ padding: 0, zIndex: 9999 }}
        />
      </CardCheckboxWrapper>
      {isImage && data ? (
        <StyledImage
          src={data}
          alt={file.display_name}
          onLoad={() => window.URL.revokeObjectURL(data)}
        />
      ) : file.type === 'video' ? (
        <VideoW>
          {videoThumbnailurl && <StyledVideo src={videoThumbnailurl} />}
          <PlayButtonContainer>
            <PlayIcon />
          </PlayButtonContainer>
        </VideoW>
      ) : (
        <CardIconWrapper>{getFileIcon(file)}</CardIconWrapper>
      )}
      <CardTitle>{file.display_name}</CardTitle>
      {authorName && <CardMeta>{authorName}</CardMeta>}
      {dateStr && <CardMeta>{dateStr}</CardMeta>}
    </FileCardWrapper>
  );
}

function getFileIcon(file: FileItem): ReactElement {
  if (VIDEO_MIME_TYPES.includes(file.type) || file.type === 'video') {
    return <VideocamOutlinedIcon sx={{ fontSize: 48, color: theme.palette.info.main }} />;
  }
  const ext = file.file_extension.split('.').pop()?.toLowerCase() ?? '';
  const svgSx = { width: 40, height: 40 };
  if (ext === 'pdf') return <PictureAsPdfIcon sx={{ fontSize: 48, color: '#e53935' }} />;
  if (ext === 'doc' || ext === 'docx') return <WordIcon style={svgSx} />;
  if (ext === 'ppt' || ext === 'pptx') return <PPTIcon style={svgSx} />;
  if (ext === 'xls' || ext === 'xlsx') return <ExcelIcon style={svgSx} />;
  if (ext === 'csv') return <CSVIcon style={svgSx} />;
  if (ext === 'txt') return <TextIcon style={svgSx} />;
  return <InsertDriveFileOutlinedIcon sx={{ fontSize: 48, color: '#217346' }} />;
}

function filterByTab(files: FileItem[], tab: GalleryTab): FileItem[] {
  if (tab === 'images') {
    return files.filter((f) => IMAGE_MIME_TYPES.includes(f.type) || f.type === 'image');
  }
  if (tab === 'videos') {
    return files.filter((f) => VIDEO_MIME_TYPES.includes(f.type) || f.type === 'video');
  }
  return files.filter(
    (f) =>
      !IMAGE_MIME_TYPES.includes(f.type) &&
      f.type !== 'image' &&
      !VIDEO_MIME_TYPES.includes(f.type) &&
      f.type !== 'video',
  );
}

interface FileGalleryModalProps {
  open: boolean;
  onClose: () => void;
  categoryIds: string[];
  onAdd: (files: FileItem[]) => void;
}

export function FileGalleryModal({
  open,
  onClose,
  categoryIds,
  onAdd,
}: FileGalleryModalProps): ReactElement {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<GalleryTab>('documents');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<FileItem[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (!open) {
      setSearchText('');
      setDebouncedSearch('');
      setSelectedFiles([]);
      setActiveTab('documents');
    }
  }, [open]);

  const { files, isLoading } = useFilesGalleryQuery({
    categoryIds,
    searchText: debouncedSearch,
  });

  const visibleFiles = filterByTab(files, activeTab);

  function toggleFile(file: FileItem) {
    setSelectedFiles((prev) =>
      prev.some((f) => f.id === file.id) ? prev.filter((f) => f.id !== file.id) : [...prev, file],
    );
  }

  function handleAdd() {
    onAdd(selectedFiles);
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      sx={{ '& .MuiDialog-paper': { width: 800, height: 650 } }}
    >
      <ModalHeader>
        <ModalTitle>{t('mediaExplorer.title')}</ModalTitle>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: 14,
              color: '#999',
              fontWeight: 400,
              minWidth: 'auto',
              padding: '8px 16px',
            },
            '& .MuiTab-root.Mui-selected': {
              color: theme.palette.info.main,
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.info.main,
            },
          }}
        >
          <Tab label={t('mediaExplorer.tabs.documents')} value="documents" />
          <Tab label={t('mediaExplorer.tabs.images')} value="images" />
          <Tab label={t('mediaExplorer.tabs.videos')} value="videos" />
        </Tabs>
      </ModalHeader>

      <SearchWrapper>
        <TextField
          fullWidth
          placeholder={t('mediaExplorer.searchPlaceholder')}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#999' }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
        />
      </SearchWrapper>

      <DialogContent sx={{ paddingTop: 0 }}>
        <FileGrid>
          {isLoading && (
            <EmptyStateWrapper>
              <CircularProgress size={32} />
            </EmptyStateWrapper>
          )}
          {!isLoading && visibleFiles.length === 0 && (
            <EmptyStateWrapper>
              <CardMeta>{t('mediaExplorer.emptyState')}</CardMeta>
            </EmptyStateWrapper>
          )}
          {!isLoading &&
            visibleFiles.map((file) => (
              <GalleryFileCard
                key={file.id}
                file={file}
                isSelected={selectedFiles.some((f) => f.id === file.id)}
                onToggle={toggleFile}
              />
            ))}
        </FileGrid>
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px' }}>
        <CancelBtn variant="outlined" onClick={onClose}>
          {t('common.cancel')}
        </CancelBtn>
        <AddButton onClick={handleAdd} disabled={selectedFiles.length === 0}>
          {t('common.add')}
        </AddButton>
      </DialogActions>
    </Dialog>
  );
}
